import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreatePickUpDto, UpdatePickUpDto, PickUpDto } from '../../proxy/pick-ups/models';
import { PickUpService } from '../../proxy/pick-ups/pick-up.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ARGENTINA_PROVINCES } from '../../shared/constants/argentina-provinces';
import { SelectOption } from '../../shared/components/metro-form-select/metro-form-select.component';
import { LocalityService, LocalityCatalog } from '../../proxy/localities';

@Component({
  selector: 'app-pickup-form',
  templateUrl: './pickup-form.component.html',
  standalone: false
})
export class PickupFormComponent implements OnInit {
  @Input() pickup?: PickUpDto | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<PickUpDto>();

  form!: FormGroup;
  isEditMode = false;

  // winepassport#342 / #379 — ubicación del punto de retiro (para agrupar la landing por provincia).
  // "Otra" agregada al final (mismo patrón que sommelier-form, #343/#354) — faltaba acá,
  // por eso cityAsFreeText nunca se podía disparar desde el <select> de Provincia.
  provinceOptions: SelectOption[] = [
    ...ARGENTINA_PROVINCES.map(p => ({ value: p, label: p })),
    { value: 'Otra', label: 'Otra' },
  ];

  // winepassport#379 — combo dependiente Provincia -> Localidad (patrón de sommelier-form, #343/#354)
  private localityCatalog: LocalityCatalog = {};
  localityOptions: SelectOption[] = [];

  constructor(
    private fb: FormBuilder,
    private pickUpService: PickUpService,
    private route: ActivatedRoute,
    private router: Router,
    private localityService: LocalityService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.localityService.getAll().subscribe(catalog => {
      this.localityCatalog = catalog || {};
      this.refreshLocalityOptions();
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.pickUpService.get(Number(id)).subscribe(pickup => {
        this.pickup = pickup;
        this.patchForm(pickup);
        this.refreshLocalityOptions();
      });
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      address: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      province: [''],
      city: [''],
      isActive: [true, [Validators.required]],
      isVisible: [true, [Validators.required]],
      colppyWarehouseName:['']
    });
  }

  private patchForm(pickup: PickUpDto) {
    this.isEditMode = true;
    this.form.patchValue({
      name: pickup.name,
      address: pickup.address,
      email: pickup.email,
      province: pickup.province ?? '',
      city: pickup.city ?? '',
      isActive: pickup.isActive,
      isVisible: pickup.isVisible,
      colppyWarehouseName: pickup.colppyWarehouseName
    });
  }

  // ── Combo dependiente Provincia → Localidad (winepassport#379) ────────────
  /** true cuando la localidad se pide como texto libre: provincia "Otra", vacía,
   *  o sin localidades en el catálogo. */
  get cityAsFreeText(): boolean {
    const province = (this.form?.get('province')?.value || '').trim();
    if (!province || province === 'Otra') return true;
    return !(this.localityCatalog[province]?.length);
  }

  private refreshLocalityOptions(): void {
    const province = (this.form?.get('province')?.value || '').trim();
    const list = this.localityCatalog[province] || [];
    this.localityOptions = list.map(l => ({ value: l, label: l }));

    // Edit mode: si la localidad persistida no matchea ninguna opción del catálogo
    // (casing / tilde distinta de una carga vieja) se inyecta como opción para que el
    // <select> la muestre en vez de verse vacío. El usuario puede reelegir del catálogo.
    const current = (this.form?.get('city')?.value || '').trim();
    if (current && !this.localityOptions.some(o => o.value.toLowerCase() === current.toLowerCase())) {
      this.localityOptions = [{ value: current, label: current }, ...this.localityOptions];
    }
  }

  onProvinceChange(value: string): void {
    // El (selectionChange) de metro-form-select se dispara DESPUÉS de que el CVA ya
    // escribió el control `province`; acá solo se reacciona.
    // Si la localidad cargada no pertenece a la nueva provincia, se limpia.
    const city = (this.form.get('city')?.value || '').trim();
    const list = this.localityCatalog[value] || [];
    if (city && list.length && !list.some(l => l.toLowerCase() === city.toLowerCase())) {
      this.form.get('city')?.setValue('');
    }
    this.refreshLocalityOptions();
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (control?.invalid && control?.touched) {
      if (control.errors?.['required']) return '::ThisFieldIsRequired';
      if (control.errors?.['email']) return '::InvalidEmailFormat';
    }
    return undefined;
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value as CreatePickUpDto | UpdatePickUpDto;

    if (this.isEditMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.pickUpService.update(Number(id), formValue as UpdatePickUpDto)
          .subscribe(result => this.handleSuccess(result));
      }
    } else {
      this.pickUpService.create(formValue as CreatePickUpDto)
        .subscribe(result => this.handleSuccess(result));
    }
  }

  onCancel() {
    this.router.navigate(['/backoffice/pickuppoints']);
  }

  private handleSuccess(result: PickUpDto) {
    this.saved.emit(result);
    this.router.navigate(['/backoffice/pickuppoints']);
  }
}
